import React, { Component, createRef } from "react";

const QUICK_RANGES = [
  { label: "Today", value: "today" },
  { label: "This Week", value: "thisWeek" },
  { label: "Next Week", value: "nextWeek" },
  { label: "This Month", value: "thisMonth" },
  { label: "Custom Range", value: "custom" },
];

class DateFilter extends Component {
  constructor(props) {
    super(props);

    this.state = {
      selectedRange: "",
      startDate: null,
      endDate: null,
      showCalendar: false,
      dropdownOpen: false,
      buttonLabel: "Date",
    };

    this.wrapperRef = createRef();
    this.triggerRef = createRef();
    this.panelRef = createRef();
    this.quickRangeRefs = QUICK_RANGES.reduce((refs, range) => {
      refs[range.value] = createRef();
      return refs;
    }, {});
    this.lastCustomRange = { startDate: null, endDate: null };
    this.handleDocumentClick = this.handleDocumentClick.bind(this);
    this.handleClearFilters = this.handleClearFilters.bind(this);
    this.handleTriggerKeyDown = this.handleTriggerKeyDown.bind(this);
    this.handlePanelKeyDown = this.handlePanelKeyDown.bind(this);
    this.initializing = true;
    this._isMounted = false;
    this._pendingTimeouts = [];
  }

  componentDidMount() {
    this._isMounted = true;
    document.addEventListener("mousedown", this.handleDocumentClick);
    document.addEventListener("keydown", this.handleEscapeKeyDown);
    this.handleFocusOut = (e) => {
      if (this.state.dropdownOpen && !this.wrapperRef.current?.contains(e.relatedTarget)) {
        this.setState({ dropdownOpen: false });
      }
    };
    this.wrapperRef.current?.addEventListener("focusout", this.handleFocusOut);
    const initTimeout = setTimeout(() => {
      this.attachDatePickerListeners();
      this.disableRadioButtonFocus();
      this.initializeFromUrl();
    }, 0);
    this._pendingTimeouts.push(initTimeout);
  }

  componentWillUnmount() {
    this._isMounted = false;
    document.removeEventListener("mousedown", this.handleDocumentClick);
    document.removeEventListener("keydown", this.handleEscapeKeyDown);
    this.wrapperRef.current?.removeEventListener("focusout", this.handleFocusOut);
    this._radioObserver?.disconnect();
    // Clear any pending timeouts to prevent setState on unmounted component
    this._pendingTimeouts.forEach(timeoutId => clearTimeout(timeoutId));
    this._pendingTimeouts = [];
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.selectedRange !== this.state.selectedRange) {
      if (!this.initializing && this.state.selectedRange) {
        this.applyQuickRange();
      }
    }

    if (
      prevState.showCalendar !== this.state.showCalendar &&
      this.state.showCalendar &&
      this.state.selectedRange === "custom"
    ) {
      this.syncDatePickerFromState();
    }

    if (!prevState.dropdownOpen && this.state.dropdownOpen) {
      if (this.state.showCalendar && this.state.selectedRange === "custom") {
        this.syncDatePickerFromState();
      }
      requestAnimationFrame(() => {
        this.focusQuickRange(this.state.selectedRange || QUICK_RANGES[0].value);
      });
    }
  }

  // ----------------------- Initialization -----------------------
  initializeFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const startParam = params.get("startDate");
    const endParam = params.get("endDate");

    let startCopy = startParam ? this.parseUrlDate(startParam) : null;
    let endCopy = endParam ? this.parseUrlDate(endParam) : null;

    let selectedRange = "";
    let showCalendar = false;

    if (startCopy || endCopy) {
      selectedRange = "custom";
      showCalendar = true;

      const today = new Date();
      if (startCopy && endCopy) {
        if (this.isSameDay(startCopy, today) && this.isSameDay(endCopy, today)) {
          selectedRange = "today";
          showCalendar = false;
        } else if (this.isSameDayRange(startCopy, endCopy, this.getThisWeekRange())) {
          selectedRange = "thisWeek";
          showCalendar = false;
        } else if (this.isSameDayRange(startCopy, endCopy, this.getNextWeekRange())) {
          selectedRange = "nextWeek";
          showCalendar = false;
        } else if (this.isSameDayRange(startCopy, endCopy, this.getThisMonthRange())) {
          selectedRange = "thisMonth";
          showCalendar = false;
        }
      }
    }

    this.setState(
      {
        startDate: startCopy,
        endDate: endCopy,
        selectedRange,
        showCalendar,
      },
      () => {
        this.updateButtonLabel();
        if (selectedRange === "custom" && startCopy && endCopy) {
          this.updateUrl(startCopy, endCopy);
          const dp = document.getElementById("datePickerEl");
          if (dp) {
            dp.value = [this.formatIsoDate(startCopy), this.formatIsoDate(endCopy)];
            dp.start = startCopy;
            dp.end = endCopy;
          }
        }
        this.emitChange();
        if (selectedRange === "custom") {
          this.lastCustomRange = { startDate: startCopy, endDate: endCopy };
        }
        this.initializing = false;
      }
    );
  }

  // ----------------------- Helpers -----------------------
  parseUrlDate(str) {
    if (!str) return null;
    if (str.includes("/")) {
      const [month, day, year] = str.split("/").map(Number);
      return new Date(year, month - 1, day);
    } else if (str.includes("-")) {
      const [year, month, day] = str.split("-").map(Number);
      return new Date(year, month - 1, day);
    }
    return new Date(str);
  }

  formatDateWithSlash(date) {
    if (!date) return "";
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  }

  formatIsoDate(date) {
    if (!date) return "";
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }

  isSameDay(d1, d2) {
    return d1 &&
    d2 &&
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate();
  }

  isSameDayRange(start, end, [rangeStart, rangeEnd]) {
    return this.isSameDay(start, rangeStart) && this.isSameDay(end, rangeEnd);
  }

  getThisWeekRange() {
    const today = new Date();
    const start = new Date(today);
    start.setDate(today.getDate() - today.getDay());
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    return [start, end];
  }

  getNextWeekRange() {
    const today = new Date();
    const start = new Date(today);
    start.setDate(today.getDate() - today.getDay() + 7);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    return [start, end];
  }

  getThisMonthRange() {
    const today = new Date();
    const start = new Date(today.getFullYear(), today.getMonth(), 1);
    const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return [start, end];
  }

  getQuickRangeValues() {
    return QUICK_RANGES.map((range) => range.value);
  }

  focusQuickRange(value) {
    const fallbackValue = this.getQuickRangeValues()[0];
    const ref = this.quickRangeRefs[value] || this.quickRangeRefs[fallbackValue];
    ref?.current?.focus();
  }

  openDropdown(focusValue = this.state.selectedRange || QUICK_RANGES[0].value) {
    this.setState({ dropdownOpen: true }, () => {
      requestAnimationFrame(() => {
        this.focusQuickRange(focusValue);
      });
    });
  }

  closeDropdown(restoreFocus = true) {
    this.setState({ dropdownOpen: false }, () => {
      if (restoreFocus) {
        requestAnimationFrame(() => {
          this.triggerRef.current?.setFocus?.();
        });
      }
    });
  }

  selectRange(rangeValue) {
    if (rangeValue === "custom") {
      const { startDate, endDate } = this.lastCustomRange;
      this.setState({
        selectedRange: "custom",
        startDate: startDate || null,
        endDate: endDate || null,
      });
      return;
    }

    this.setState({ selectedRange: rangeValue });
  }

  // ----------------------- Event Handlers -----------------------
  handleDocumentClick(event) {
    if (this.wrapperRef.current && !this.wrapperRef.current.contains(event.target)) {
      this.setState({ dropdownOpen: false });
    }
  }

  handleEscapeKeyDown = (event) => {
    if (event.key === "Escape" && this.state.dropdownOpen) {
      if (this.wrapperRef.current?.contains(document.activeElement)) {
        event.preventDefault();
        this.closeDropdown();
      }
    }
  };

  handleTriggerKeyDown(event) {
    if (["ArrowDown", "Enter", " "].includes(event.key)) {
      event.preventDefault();
      this.openDropdown();
    }
    if (event.key === "ArrowUp" && this.state.dropdownOpen) {
      event.preventDefault();
      this.closeDropdown();
    }
  }

  handlePanelKeyDown(event) {
    const quickRangeValues = this.getQuickRangeValues();
    const activeElement = document.activeElement;
    const activeIndex = quickRangeValues.findIndex(
      (value) => this.quickRangeRefs[value].current === activeElement
    );

    if (activeIndex === -1) {
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      const nextValue = quickRangeValues[(activeIndex + 1) % quickRangeValues.length];
      this.focusQuickRange(nextValue);
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      const nextIndex = activeIndex === 0 ? quickRangeValues.length - 1 : activeIndex - 1;
      this.focusQuickRange(quickRangeValues[nextIndex]);
    }

    if (event.key === "Home") {
      event.preventDefault();
      this.focusQuickRange(quickRangeValues[0]);
    }

    if (event.key === "End") {
      event.preventDefault();
      this.focusQuickRange(quickRangeValues[quickRangeValues.length - 1]);
    }
  }

  updateButtonLabel() {
    const { selectedRange, startDate, endDate } = this.state;
    let label = "Date";

    if (selectedRange === "custom") {
      if (startDate && endDate) {
        label = `${this.formatDateWithSlash(startDate)} - ${this.formatDateWithSlash(endDate)}`;
      } else if (startDate) {
        label = this.formatDateWithSlash(startDate);
      } else if (endDate) {
        label = this.formatDateWithSlash(endDate);
      }
    } else {
      switch (selectedRange) {
        case "today":
          label = "Today";
          break;
        case "thisWeek":
          label = "This Week";
          break;
        case "nextWeek":
          label = "Next Week";
          break;
        case "thisMonth":
          label = "This Month";
          break;
      }
    }

    this.setState({ buttonLabel: label });
  }

  updateUrl(start, end) {
    const params = new URLSearchParams(window.location.search);

    if (!start && !end) {
      params.delete("startDate");
      params.delete("endDate");
    } else {
      if (start) params.set("startDate", this.formatDateWithSlash(start));
      else params.delete("startDate");
      if (end) params.set("endDate", this.formatDateWithSlash(end));
      else params.delete("endDate");
    }
    params.delete("p");

    window.history.replaceState(
      {},
      "",
      `${window.location.pathname}${params.toString() ? "?" + params.toString() : ""}`
    );
  }

  applyQuickRange() {
    const { selectedRange } = this.state;
    let start = null;
    let end = null;
    let showCalendar = false;

    switch (selectedRange) {
      case "today":
        start = new Date();
        end = new Date();
        break;
      case "thisWeek":
        [start, end] = this.getThisWeekRange();
        break;
      case "nextWeek":
        [start, end] = this.getNextWeekRange();
        break;
      case "thisMonth":
        [start, end] = this.getThisMonthRange();
        break;
      case "custom": {
        showCalendar = true;
        const picker = document.getElementById("datePickerEl");
        const range = this.normalizePickerValue(picker?.value);
        start = range[0] ? this.parseUrlDate(range[0]) : null;
        end = range[1] ? this.parseUrlDate(range[1]) : null;
        break;
      }
    }

    this.setState({ startDate: start, endDate: end, showCalendar }, () => {
      if (selectedRange === "custom" && (start || end)) {
        this.lastCustomRange = { startDate: start, endDate: end };
      }
      this.updateUrl(start, end);
      this.updateButtonLabel();
      this.emitChange();
    });
  }

  normalizePickerValue(value) {
    if (!value) return [null, null];

    if (Array.isArray(value)) {
      return value;
    }

    if (typeof value === "string") {
      const parts = value
        .split(/\s*(?:to|,)\s*/i)
        .map((part) => part.trim())
        .filter(Boolean);
      return parts;
    }

    if (typeof value === "object") {
      const first = value[0] || value.start || value.startDate || value.from || null;
      const second = value[1] || value.end || value.endDate || value.to || null;
      // Return fixed 2-tuple to preserve start/end semantics; callers handle nulls
      return [first, second];
    }

    return [null, null];
  }

  attachDatePickerListeners() {
    const dp = document.getElementById("datePickerEl");
    if (!dp) return;

    if (dp._dateFilterListenerAttached) return;
    dp._dateFilterListenerAttached = true;

    const handleDateChange = () => {
      // Guard against setState on unmounted component
      if (!this._isMounted) return;
      const datesRange = this.normalizePickerValue(dp.value);
      const rangeStart = datesRange[0] ? this.parseUrlDate(datesRange[0]) : null;
      const rangeEnd = datesRange[1] ? this.parseUrlDate(datesRange[1]) : null;

      const hasAnyDate = !!(rangeStart || rangeEnd);

      this.setState(
        {
          selectedRange: hasAnyDate ? "custom" : "",
          startDate: rangeStart,
          endDate: rangeEnd,
          showCalendar: hasAnyDate,
        },
        () => {
          this.lastCustomRange = { startDate: rangeStart, endDate: rangeEnd };
          this.updateUrl(rangeStart, rangeEnd);
          this.updateButtonLabel();
          this.emitChange();
        }
      );
    };

    const attachListener = () => {
      if (dp.value == null) {
        dp.value = [];
      }

      // Mouse/calendar selection
      dp.addEventListener("calciteInputDatePickerChange", handleDateChange);
    };

    if (typeof dp.componentOnReady === "function") {
      dp.componentOnReady().then(attachListener);
    } else {
      attachListener();
    }
  }

  disableRadioButtonFocus() {
    if (!this.panelRef.current) return;
    const radios = this.panelRef.current.querySelectorAll("calcite-radio-button");

    const forceTabIndexNegative = (el) => {
      if (el.getAttribute("tabindex") !== "-1") {
        el.setAttribute("tabindex", "-1");
      }
      const inner = el.shadowRoot?.querySelector("input, [tabindex]");
      if (inner && inner.getAttribute("tabindex") !== "-1") {
        inner.setAttribute("tabindex", "-1");
      }
    };

    // MutationObserver re-enforces tabindex whenever Calcite overrides it
    this._radioObserver = new MutationObserver((mutations) => {
      mutations.forEach((m) => {
        if (m.type === "attributes" && m.attributeName === "tabindex") {
          forceTabIndexNegative(m.target);
        }
      });
    });

    radios.forEach((rb) => {
      forceTabIndexNegative(rb);
      this._radioObserver.observe(rb, { attributes: true, attributeFilter: ["tabindex"] });

      if (typeof rb.componentOnReady === "function") {
        rb.componentOnReady().then(() => {
          forceTabIndexNegative(rb);
        });
      }
    });
  }

  syncDatePickerFromState() {
    const dp = document.getElementById("datePickerEl");
    if (!dp) return;

    const { startDate, endDate } = this.state;
    const values = [];

    if (startDate) {
      values.push(this.formatIsoDate(startDate));
    }

    if (endDate) {
      values.push(this.formatIsoDate(endDate));
    }

    dp.value = values.length ? values : [];
    dp.start = startDate || null;
    dp.end = endDate || null;
  }

  emitChange() {
    const { onDateChange } = this.props;
    if (onDateChange) {
      onDateChange({
        startDate: this.state.startDate,
        endDate: this.state.endDate,
        range: this.state.selectedRange,
      });
    }
  }

  handleClearFilters() {
    this.lastCustomRange = { startDate: null, endDate: null };
    this.setState({ selectedRange: "", startDate: null, endDate: null, showCalendar: false, buttonLabel: "Date" }, () => {
      this.updateUrl(null, null);
      const dp = document.getElementById("datePickerEl");
      if (dp) {
        dp.value = [];
        dp.start = null;
        dp.end = null;
      }
      this.emitChange();
    });
  }

  render() {
    const { showCalendar, buttonLabel, dropdownOpen, selectedRange } = this.state;
    const showClearIcon = !!(
      this.state.selectedRange ||
      this.state.startDate ||
      this.state.endDate
    );

    return (
      <div
        className="date-filter"
        ref={this.wrapperRef}
      >
        <div className="dropdown-wrapper">
          <div className="dateFilter-dropdown">
            <calcite-button
              ref={this.triggerRef}
              icon-start="calendar"
              appearance="outline"
              kind="neutral"
              alignment="start"
              width="full"
              aria-haspopup="dialog"
              aria-expanded={dropdownOpen ? "true" : "false"}
              aria-controls="dateFilterPanel"
              onClick={() => {
                if (dropdownOpen) {
                  this.closeDropdown(false);
                } else {
                  this.openDropdown();
                }
              }}
              onKeyDown={this.handleTriggerKeyDown}
            >
              {buttonLabel}
              {showClearIcon && (
                <calcite-icon
                  icon="x"
                  scale="m"
                  role="button"
                  tabIndex="0"
                  aria-label="Clear date filter"
                  onClick={(e) => {
                    e.stopPropagation();
                    this.handleClearFilters();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      e.stopPropagation();
                      this.handleClearFilters();
                    }
                  }}
                />
              )}
            </calcite-button>

              <div
                id="dateFilterPanel"
                className="dateFilter-dropdown__panel"
                ref={this.panelRef}
                role="radiogroup"
                aria-labelledby="dateFilterGroupTitle"
                style={{ display: dropdownOpen ? "block" : "none" }}
                onKeyDown={this.handlePanelKeyDown}
              >
                <div className="dateFilter-dropdown__group">
                  <span id="dateFilterGroupTitle" className="dateFilter-dropdown__group-title">Date</span>
                  {QUICK_RANGES.map((range) => {
                    const isSelected = selectedRange === range.value;

                    return (
                      <button
                        key={range.value}
                        ref={this.quickRangeRefs[range.value]}
                        type="button"
                        role="radio"
                        aria-checked={isSelected}
                        className={`dateFilter-dropdown__item${isSelected ? " dateFilter-dropdown__item--selected" : ""}`}
                        onClick={() => {
                          this.selectRange(range.value);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            this.selectRange(range.value);
                          }
                        }}
                      >
                        <span className="dateFilter-dropdown__item-content">
                          <calcite-radio-button value={range.value} checked={isSelected || undefined} tabIndex="-1" aria-hidden="true"></calcite-radio-button>
                          <span>{range.label}</span>
                        </span>
                      </button>
                    );
                  })}

                  <div className="custom-calendar" style={{ display: showCalendar ? "block" : "none" }}>
                    <calcite-label>
                      Select dates
                      <calcite-input-date-picker
                        id="datePickerEl"
                        range
                        calendars="1"
                        layout="vertical"
                        overlay-positioning="fixed"
                        placement="bottom-start"
                        onKeyDown={(e) => {
                          if (e.key === "Escape") {
                            e.preventDefault();
                            e.stopPropagation();
                            this.closeDropdown();
                          }
                        }}
                      ></calcite-input-date-picker>
                    </calcite-label>
                  </div>
                </div>

                <div className="dateFilter-dropdown__group dateFilter-dropdown__group--actions">
                  <calcite-link
                    onClick={this.handleClearFilters}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        this.handleClearFilters();
                      }
                      if (e.key === "Tab" && !e.shiftKey) {
                        this.closeDropdown();
                      }
                    }}
                  >
                    Clear filters
                  </calcite-link>
                </div>
              </div>
          </div>
        </div>
      </div>
    );
  }
}

export default DateFilter;
